import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement} [viewAllButton] - The "view all products" submit button.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * The button's server-rendered label, restored whenever the search is reset.
   * @type {string}
   */
  #viewAllDefaultLabel = '';

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    this.#viewAllDefaultLabel = this.refs.viewAllButton?.textContent?.trim() ?? '';

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.ps-search__product-grid, ' +
          '.ps-search__article-list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          // Enter with nothing highlighted opens the full search page for the
          // term — products and articles alike. `preventDefault` is what makes
          // that deterministic: without it the form submits too, and the form
          // carries the hidden `type=product` input the "View all products" CTA
          // needs, so the browser would race two navigations to different URLs.
          event.preventDefault();

          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Update the "view all products" button to name the current search term.
   * The template comes from a locale string with a `[[terms]]` placeholder.
   * @param {string} searchTerm - The term to interpolate.
   */
  #updateViewAllLabel(searchTerm) {
    const { viewAllButton } = this.refs;
    const template = viewAllButton?.dataset.labelTemplate;

    if (!viewAllButton || !template) return;

    // An empty term means the search was reset: restore the generic label rather
    // than leave the previous shopper's query on the button.
    viewAllButton.textContent = searchTerm
      ? template.replace('[[terms]]', () => searchTerm)
      : this.#viewAllDefaultLabel;
  }

  /**
   * Rewrite the live region to announce what is actually on screen.
   *
   * The section renders its own count, but it only ever sees one request's
   * resources: the products response carries no articles, so it would announce
   * "no results" over grafted recipes, or undercount a mixed result set.
   * @param {Element} inner - The parsed results container, before it is morphed in.
   */
  #updateStatus(inner) {
    const status = inner.querySelector('[role="status"]');
    if (!status) return;

    const template = status.dataset.statusTemplate;
    const count = inner.querySelectorAll('.ps-search-product-card, .ps-search-article-card').length;

    if (!count || !template) return;

    status.textContent = template.replace('[[count]]', () => String(count));
  }

  /**
   * Morph the results container, keeping the section's `#predictive-search-results`
   * wrapper in the DOM.
   *
   * `morph` defaults to `childrenOnly`, so it replaces the target's children with
   * the children of whatever it is handed. Passing the wrapper itself would splice
   * its children straight into the container and drop the wrapper — along with its
   * `id` (the target of the input's `aria-controls`/`aria-owns`), its
   * `role="listbox"`, and the `predictive-search-dropdown` class that several
   * selectors key off. Nesting it one level down keeps it.
   * @param {Element} base - The parsed `#predictive-search-results` element.
   */
  #morphResults(base) {
    const container = document.createElement('div');
    container.appendChild(base);

    morph(this.refs.predictiveSearchResults, container);
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    // Fetch the same section three times with different queries. The predictive
    // search API caps articles at 10 per request, so recipes and blog posts are
    // requested separately (via the `tag:recipes` / `-tag:recipes` operators) to
    // give each its own 10-item budget instead of sharing a single pool.
    const fetchSection = (query, type) => {
      const url = new URL(Theme.routes.predictive_search_url, location.origin);
      url.searchParams.set('q', query);
      url.searchParams.set('resources[limit_scope]', 'each');
      url.searchParams.set('resources[type]', type);
      return sectionRenderer.getSectionHTML(sectionId, false, url);
    };

    try {
      const settled = await Promise.allSettled([
        fetchSection(searchTerm, 'product,query'),
        fetchSection(`${searchTerm} tag:recipes`, 'article'),
        fetchSection(`${searchTerm} -tag:recipes`, 'article'),
      ]);

      // A failed article request must not take the whole dropdown down with it:
      // degrade to whatever succeeded. A failed products request leaves no
      // skeleton to graft into and is handled by the `!productsMarkup` guard below.
      /** @type {[string | null, string | null, string | null]} */
      const [productsMarkup, recipesMarkup, blogMarkup] = settled.map((result) =>
        result.status === 'fulfilled' ? result.value : null
      );

      if (abortController.signal.aborted) return;
      if (!productsMarkup) return;

      /**
       * @param {string | null} [markup] - The markup to parse.
       * @returns {Document} The parsed document.
       */
      const parse = (markup) => new DOMParser().parseFromString(markup ?? '', 'text/html');

      // The products request renders the skeleton (queries + products + messaging).
      const base = parse(productsMarkup).querySelector('#predictive-search-results');
      if (!base) return;

      const inner = base.querySelector('.predictive-search-results__inner');
      if (!inner) {
        this.#morphResults(base);
        this.#resetScrollPositions();
        return;
      }

      // Graft the dedicated recipes / blog groups into the aside column. The
      // products request renders an empty aside, so there is nothing to clear.
      const aside = inner.querySelector('[data-search-aside]');

      const recipes = parse(recipesMarkup).querySelector('[data-search-group="recipes"]');
      const blog = parse(blogMarkup).querySelector('[data-search-group="blog"]');

      if (aside && (recipes || blog)) {
        // With articles added this is no longer a single-product result set.
        inner.querySelector('[data-single-result-url]')?.remove();
        if (recipes) aside.appendChild(recipes);
        if (blog) aside.appendChild(blog);

        // Remove the "no results" note if any group ended up with content.
        inner.querySelector('.predictive-search-results__no-results')?.remove();
      }

      // Announce the assembled result set, not just the products request's slice.
      this.#updateStatus(inner);

      this.#updateViewAllLabel(searchTerm);

      this.#morphResults(base);
      this.#resetScrollPositions();
    } catch (error) {
      if (abortController.signal.aborted) return;
      // The only caller (`search`) is a debounced handler that does not await or
      // catch this method's promise, so rethrowing here would surface as an
      // unhandled rejection instead of anything a user or developer sees. Log it.
      console.error(error);
    }
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();
    this.#updateViewAllLabel('');

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
